import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { APP, ROUTES, STORAGE_KEYS } from '../../config';
import { useAuth } from '../../lib/auth';
import { ErrorBox } from '../../components/Loader.jsx';
import { PublicComplaintsAPI } from '../../lib/publicComplaints';
import { executeRecaptchaV3 } from '../../lib/recaptcha';
import { buildLoginExtras } from '../../lib/loginSecurity';

function persistSession(data, setUser) {
  if (!data || !data.access) {
    throw new Error('Sign-in response missing access token.');
  }
  localStorage.setItem(STORAGE_KEYS.ACCESS, data.access);
  if (data.refresh) {
    localStorage.setItem(STORAGE_KEYS.REFRESH, data.refresh);
  }
  if (data.user) {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
    setUser(data.user);
  }
}

async function withCaptcha(captcha, body) {
  if (!captcha || !captcha.site_key) return body;
  const token = await executeRecaptchaV3(
    captcha.site_key,
    captcha.action || 'public_complaint',
  );
  return { ...body, 'g-recaptcha-response': token };
}

/**
 * Login / create-account screen shown at ``/complaints`` when the org
 * admin has enabled public complaint access and the visitor is not
 * signed in.  If the setting is off, visitors are sent to ``/login``
 * — the historic staff-only behaviour.
 */
export default function PublicComplaintsAuth() {
  const { setUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [captcha, setCaptcha] = useState(null);
  const [mode, setMode] = useState('login');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const payload = await PublicComplaintsAPI.access();
        if (cancelled) return;
        setEnabled(Boolean(payload.enabled));
        setOrgName(payload.organisation_name || '');
        setCaptcha(payload.captcha || null);
      } catch {
        if (!cancelled) setEnabled(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="ty-fullscreen-center">
        <div className="ty-spinner" aria-label="Loading" />
      </div>
    );
  }

  if (!enabled) {
    return <Navigate to={ROUTES.LOGIN} replace state={{ from: location }} />;
  }

  const afterAuth = (data, { isSignup } = {}) => {
    persistSession(data, setUser);
    const serverNext =
      typeof data.redirect_to === 'string' && data.redirect_to.startsWith('/')
        ? data.redirect_to
        : null;
    const current = location.pathname || '';
    const deepLink = current.startsWith('/complaints/') ? current : null;
    if (isSignup) {
      navigate(serverNext || '/complaints/create', { replace: true });
      return;
    }
    navigate(deepLink || serverNext || '/complaints/create', { replace: true });
  };

  const onLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const extras = await buildLoginExtras();
      const body = await withCaptcha(captcha, {
        email: email.trim().toLowerCase(),
        password,
        ...extras,
      });
      const data = await PublicComplaintsAPI.login(body);
      afterAuth(data);
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const onSignup = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    if (password !== confirm) {
      setError(new Error('Passwords do not match.'));
      setSubmitting(false);
      return;
    }
    try {
      const extras = await buildLoginExtras();
      const body = await withCaptcha(captcha, {
        email: email.trim().toLowerCase(),
        password,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone_number: phone.trim(),
        ...extras,
      });
      const data = await PublicComplaintsAPI.signup(body);
      afterAuth(data, { isSignup: true });
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const title = orgName ? `${orgName} complaints` : 'File a complaint';

  return (
    <div className="ty-login">
      <div className="ty-login__card">
        <div
          aria-hidden
          style={{
            width: 64,
            height: 64,
            margin: '0 auto 14px',
            borderRadius: 18,
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
            fontSize: 28,
            fontWeight: 800,
            letterSpacing: '-0.02em',
            background: 'linear-gradient(135deg, #7980ff 0%, #656cfd 100%)',
            boxShadow: '0 12px 28px rgba(121, 128, 255, 0.35)',
          }}
        >
          {(APP.name || 'T').slice(0, 1).toUpperCase()}
        </div>
        <h1 className="ty-login__title">{title}</h1>
        <p className="ty-login__sub">
          Create a complaints-only account or sign in to file a ticket.
        </p>

        <div
          role="tablist"
          aria-label="Public complaint access"
          style={{
            display: 'flex',
            gap: 8,
            marginBottom: 20,
          }}
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'login'}
            className={mode === 'login' ? 'ty-btn' : 'ty-btn ty-btn--ghost'}
            onClick={() => { setMode('login'); setError(null); }}
            style={{ flex: 1 }}
          >
            Sign in
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'signup'}
            className={mode === 'signup' ? 'ty-btn' : 'ty-btn ty-btn--ghost'}
            onClick={() => { setMode('signup'); setError(null); }}
            style={{ flex: 1 }}
          >
            Create account
          </button>
        </div>

        <ErrorBox error={error} />

        {mode === 'login' ? (
          <form onSubmit={onLogin}>
            <div className="ty-field">
              <label htmlFor="pc-email">Email</label>
              <input
                id="pc-email"
                className="ty-input"
                type="email"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="ty-field">
              <label htmlFor="pc-password">Password</label>
              <input
                id="pc-password"
                className="ty-input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {captcha ? (
              <p className="ty-login__sub">
                Protected by reCAPTCHA.
              </p>
            ) : null}
            <button className="ty-btn" type="submit" disabled={submitting} style={{ width: '100%' }}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        ) : (
          <form onSubmit={onSignup}>
            <div className="ty-field">
              <label htmlFor="pc-first">First name</label>
              <input
                id="pc-first"
                className="ty-input"
                type="text"
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="ty-field">
              <label htmlFor="pc-last">Last name</label>
              <input
                id="pc-last"
                className="ty-input"
                type="text"
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
            <div className="ty-field">
              <label htmlFor="pc-signup-email">Email</label>
              <input
                id="pc-signup-email"
                className="ty-input"
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="ty-field">
              <label htmlFor="pc-phone">Phone (optional)</label>
              <input
                id="pc-phone"
                className="ty-input"
                type="tel"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            <div className="ty-field">
              <label htmlFor="pc-new-password">Password</label>
              <input
                id="pc-new-password"
                className="ty-input"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
              />
            </div>
            <div className="ty-field">
              <label htmlFor="pc-confirm">Confirm password</label>
              <input
                id="pc-confirm"
                className="ty-input"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                minLength={8}
                required
              />
            </div>
            {captcha ? (
              <p className="ty-login__sub">
                Protected by reCAPTCHA.
              </p>
            ) : null}
            <button className="ty-btn" type="submit" disabled={submitting} style={{ width: '100%' }}>
              {submitting ? 'Creating account…' : 'Create account'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
